import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { AlertCircle, ChevronLeft, RefreshCw, Settings2 } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useAuth } from '@/context/auth-context';

// ============================================================================
// Types — camelCase DTO shapes from backend mapSite/mapCategory/mapProduct
// (offering-dev commit d3643d026, route mounted at /api/admin/catalog-health)
// ============================================================================

export type HealthTier = 'AAA' | 'AA' | 'A' | 'B' | 'C';

// GET /api/admin/catalog-health/sites
export interface SiteRow {
  siteId: string;
  siteName: string;
  domain: string;
  productCount: number;
  cl4HighProducts: number;
  shs: number | null;           // null if site has no snapshots yet
  tier: HealthTier | null;
  snapshotDate: string | null;
}

// GET /api/admin/catalog-health/sites/:siteId/categories
export interface CategoryRow {
  categoryPath: string;
  totalProducts: number;
  cl4HighProducts: number;
  chs: number | null;           // demand-weighted category health score; null = unscored/unconfigured
  chsCl4High: number | null;
  tier: HealthTier;
  tierDistribution: Record<HealthTier, number>;
  topMissingAttrs: Array<{ attr: string; gapCount: number }>;
  missingAttrCount: number;     // sum of gapCount across topMissingAttrs
  unscoredReason: string | null; // set when category has no canonical_attributes configured
  snapshotDate: string;
  impactScore: number;          // computed: cl4HighProducts * (1 - chs); 0 for unscored rows
}

// GET /api/admin/catalog-health/sites/:siteId/categories/:path/products
export interface ProductRow {
  pfiId: string;
  productTitle: string;
  brand: string | null;
  phs: number;
  tier: HealthTier;
  cl4Label: string | null;
  missingAttrs: string[];
  malformedAttrs: Array<{ attr: string; value: string }>;
  computedAt: string;
}

// GET /api/admin/catalog-health/products/:pfiId/attributes
// Phase B: status extended with name_mismatch + truly_missing (replacing 'missing')
export interface AttributeDetail {
  pfiId: string;
  phs: number;
  tier: HealthTier;
  attributes: Array<{
    attributeName: string;
    canonicalName: string;
    isKeyAttribute: boolean;
    priorityRank: number;
    status: 'present' | 'name_mismatch' | 'truly_missing' | 'malformed' | 'excluded';
    currentValue: string | null;
  }>;
}

// ============================================================================
// Tier config
// ============================================================================

const TIER_CONFIG: Record<HealthTier, { bg: string; text: string; border: string; bar: string }> = {
  AAA: { bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300',  bar: 'bg-green-400'  },
  AA:  { bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300',   bar: 'bg-blue-400'   },
  A:   { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', bar: 'bg-yellow-400' },
  B:   { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', bar: 'bg-orange-400' },
  C:   { bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-300',    bar: 'bg-red-400'    },
};

function scoreTier(score: number): HealthTier {
  if (score >= 0.90) return 'AAA';
  if (score >= 0.75) return 'AA';
  if (score >= 0.60) return 'A';
  if (score >= 0.40) return 'B';
  return 'C';
}

function TierBadge({ tier }: { tier: HealthTier }) {
  const cfg = TIER_CONFIG[tier];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {tier}
    </span>
  );
}

function ScoreBar({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const cfg = TIER_CONFIG[scoreTier(score)];
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full ${cfg.bar}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground w-9 text-right">{pct}%</span>
    </div>
  );
}

// Tiny inline SVG sparkline (no chart dep) for per-category CHS history.
// Draws the CHS series over the timeline window; last-vs-first drives the trend colour.
function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const w = 64, h = 18, pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1; // avoid /0 on a flat series
  const stepX = (w - pad * 2) / (values.length - 1);
  const coords = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (h - pad * 2) * (1 - (v - min) / span);
    return { x, y };
  });
  const points = coords.map(c => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
  const first = values[0];
  const last = values[values.length - 1];
  const up = last >= first;
  const lastPt = coords[coords.length - 1];
  const firstPct = Math.round(first * 100);
  const lastPct = Math.round(last * 100);
  return (
    <svg
      width={w}
      height={h}
      role="img"
      aria-label={`CHS trend over ${values.length} snapshots: ${firstPct}% → ${lastPct}%`}
    >
      <title>{`${values.length} snapshots: ${firstPct}% → ${lastPct}%`}</title>
      <polyline
        points={points}
        fill="none"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        className={up ? 'stroke-green-500' : 'stroke-red-500'}
      />
      <circle cx={lastPt.x} cy={lastPt.y} r={1.75} className={up ? 'fill-green-500' : 'fill-red-500'} />
    </svg>
  );
}

// ============================================================================
// Fetch helpers
// ============================================================================

const BASE = '/api/admin/catalog-health';

async function fetchSites(): Promise<SiteRow[]> {
  const res = await authFetch(`${BASE}/sites`);
  if (!res.ok) throw new Error(`HTTP ${res.status} from ${BASE}/sites`);
  const json = await res.json();
  return Array.isArray(json) ? json : (json.data ?? []);
}

async function fetchCategories(siteId: string): Promise<CategoryRow[]> {
  const res = await authFetch(`${BASE}/sites/${siteId}/categories`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  const rows: Omit<CategoryRow, 'impactScore'>[] = Array.isArray(json) ? json : (json.data ?? []);
  return rows.map(r => ({
    ...r,
    impactScore: (r.unscoredReason || r.chs == null) ? 0 : r.cl4HighProducts * (1 - r.chs),
  }));
}

async function fetchProducts(siteId: string, categoryPath: string): Promise<ProductRow[]> {
  const encoded = encodeURIComponent(categoryPath);
  const res = await authFetch(`${BASE}/sites/${siteId}/categories/${encoded}/products?limit=100&offset=0`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json) ? json : (json.data ?? []);
}

async function fetchAttributeDetail(pfiId: string): Promise<AttributeDetail> {
  const res = await authFetch(`${BASE}/products/${pfiId}/attributes`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// GET /api/admin/catalog-health/sites/:siteId/timeline?days=N
// One row per snapshot_date (ASC), with a per-category CHS map — feeds the sparklines.
interface TimelineRow {
  snapshot_date: string;
  shs: number | null;
  total_products: number;
  cl4_high_products: number;
  categories: Record<string, { chs: number | null; tier: HealthTier; total: number; unscored: boolean }>;
}

async function fetchTimeline(siteId: string, days = 30): Promise<TimelineRow[]> {
  const res = await authFetch(`${BASE}/sites/${siteId}/timeline?days=${days}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json) ? json : (json.data ?? []);
}

// Collapse the timeline rows into categoryPath -> chs series (nulls/unscored days dropped),
// preserving snapshot order so the sparkline reads left→right oldest→newest.
function buildCategoryHistory(timeline: TimelineRow[]): Record<string, number[]> {
  // Null-prototype map: category_path is server-derived and could collide with Object.prototype
  // keys (e.g. "__proto__", "constructor") — Object.create(null) avoids that prototype hazard.
  const hist: Record<string, number[]> = Object.create(null);
  for (const row of timeline) {
    for (const [path, info] of Object.entries(row.categories || {})) {
      if (info && info.chs != null) {
        (hist[path] ||= []).push(info.chs);
      }
    }
  }
  return hist;
}

async function fetchScoringConfig(siteId: string): Promise<string[]> {
  const res = await authFetch(`${BASE}/sites/${siteId}/scoring-config`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return json.excludedAttrs ?? [];
}

async function putScoringConfig(
  siteId: string,
  excludedAttrs: string[],
): Promise<{ accepted: string[]; rejected: string[] }> {
  const res = await authFetch(`${BASE}/sites/${siteId}/scoring-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ excludedAttrs }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return { accepted: json.accepted ?? excludedAttrs, rejected: json.rejected ?? [] };
}

// ============================================================================
// Site grid
// ============================================================================

function SiteGrid({
  sites,
  onSelect,
}: {
  sites: SiteRow[];
  onSelect: (site: SiteRow) => void;
}) {
  const aaaCount = sites.filter(s => s.tier === 'AAA').length;
  const unscoredCount = sites.filter(s => s.shs === null).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>
          Sites at <TierBadge tier="AAA" />:{' '}
          <span className="font-semibold text-green-700">{aaaCount}</span> of {sites.length}
        </span>
        {unscoredCount > 0 && (
          <span className="text-orange-600">
            {unscoredCount} site{unscoredCount > 1 ? 's' : ''} not yet scored
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {sites.map(site => (
          <button
            key={site.siteId}
            onClick={() => onSelect(site)}
            className="text-left rounded-lg border bg-card shadow-sm hover:shadow-md hover:border-primary/50 transition-all p-4 space-y-3 focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <div className="space-y-0.5">
              <p className="font-semibold text-sm leading-tight line-clamp-1">{site.siteName}</p>
              <p className="text-xs text-muted-foreground truncate">{site.domain}</p>
            </div>

            {site.shs !== null && site.tier ? (
              <>
                <div className="flex items-center gap-2">
                  <TierBadge tier={site.tier} />
                  <span className="text-lg font-bold tabular-nums">{Math.round(site.shs * 100)}%</span>
                </div>
                <ScoreBar score={site.shs} />
              </>
            ) : (
              <p className="text-xs text-muted-foreground italic">Not yet scored</p>
            )}

            <p className="text-xs text-muted-foreground">
              {site.productCount.toLocaleString()} products
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Scoring config panel (excluded attrs chip list + edit modal)
// ============================================================================

function ScoringConfigPanel({
  siteId,
  excludedAttrs,
  loading,
  isSuperAdmin,
  onSaved,
}: {
  siteId: string;
  excludedAttrs: string[];
  loading: boolean;
  isSuperAdmin: boolean;
  onSaved: (newAttrs: string[]) => void;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function openEdit() {
    setDraft(excludedAttrs.join(', '));
    setSaveError(null);
    setEditOpen(true);
  }

  async function handleSave() {
    const parsed = draft
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
    setSaving(true);
    setSaveError(null);
    try {
      const { accepted, rejected } = await putScoringConfig(siteId, parsed);
      onSaved(accepted);
      if (rejected.length > 0) {
        setSaveError(`Unknown attrs ignored (not in canonical_attributes): ${rejected.join(', ')}`);
      } else {
        setEditOpen(false);
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-start gap-3 flex-wrap rounded-md border bg-muted/30 px-4 py-3">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-2">
          Excluded from scoring — customer sends canonical products only
        </p>
        {loading ? (
          <Skeleton className="h-5 w-40" />
        ) : excludedAttrs.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">None excluded</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {excludedAttrs.map(attr => (
              <span
                key={attr}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
              >
                {attr}
              </span>
            ))}
          </div>
        )}
      </div>

      {isSuperAdmin && (
        <Button variant="ghost" size="sm" onClick={openEdit} className="gap-1.5 shrink-0 text-xs">
          <Settings2 className="h-3.5 w-3.5" />
          Edit
        </Button>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit excluded attributes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Comma-separated list of canonical attribute names to exclude from PHS scoring.
              Use this when the customer deliberately omits certain attributes (e.g. colour, size)
              and they should not count against the score.
            </p>
            <Input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="e.g. color, size, weight"
              disabled={saving}
            />
            {saveError && (
              <p className="text-xs text-destructive">{saveError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Category table
// ============================================================================

function CategoryTable({
  site,
  categories,
  categoryHistory,
  categoryHistoryError,
  onSelect,
  onBack,
  excludedAttrs,
  excludedLoading,
  isSuperAdmin,
  onExcludedSaved,
}: {
  site: SiteRow;
  categories: CategoryRow[];
  categoryHistory: Record<string, number[]>;
  categoryHistoryError: string | null;
  onSelect: (cat: CategoryRow) => void;
  onBack: () => void;
  excludedAttrs: string[];
  excludedLoading: boolean;
  isSuperAdmin: boolean;
  onExcludedSaved: (attrs: string[]) => void;
}) {
  // Site-level quality summary: "X% of your categories are tier A or above" (PRD#4 deliverable 2).
  const scoredCats = categories.filter(c => !c.unscoredReason && c.chs != null);
  const aOrAboveCount = scoredCats.filter(c => c.tier === 'AAA' || c.tier === 'AA' || c.tier === 'A').length;
  const aOrAbovePct = scoredCats.length ? Math.round((aOrAboveCount / scoredCats.length) * 100) : 0;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-4 w-4" /> Sites
        </Button>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">{site.siteName}</span>
        {site.tier && <TierBadge tier={site.tier} />}
        {site.shs !== null && (
          <span className="text-sm text-muted-foreground">{Math.round(site.shs * 100)}% SHS</span>
        )}
      </div>

      {scoredCats.length > 0 && (
        <div className="rounded-md border bg-muted/30 px-4 py-2.5 text-sm flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold text-green-700">{aOrAbovePct}%</span>
          <span>of scored categories are tier</span>
          <TierBadge tier="A" />
          <span>or above</span>
          <span className="text-muted-foreground">({aOrAboveCount} of {scoredCats.length})</span>
        </div>
      )}

      {categoryHistoryError && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 flex items-center gap-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>
            Trend history unavailable ({categoryHistoryError}). Categories below are current; the
            Trend column is hidden until history can be loaded — this is a load error, not "no history".
          </span>
        </div>
      )}

      <ScoringConfigPanel
        siteId={site.siteId}
        excludedAttrs={excludedAttrs}
        loading={excludedLoading}
        isSuperAdmin={isSuperAdmin}
        onSaved={onExcludedSaved}
      />

      <p className="text-xs text-muted-foreground">
        Sorted by impact (high-demand products × quality gap). Fix the top rows first.
        Trend shows category CHS across recent daily snapshots.
      </p>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">CHS</TableHead>
              <TableHead>Trend</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead className="text-right">Products</TableHead>
              <TableHead className="text-right">CL4-High</TableHead>
              <TableHead>Top missing attrs</TableHead>
              <TableHead className="text-right">Impact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map(cat => (
              <TableRow
                key={cat.categoryPath}
                className={cat.unscoredReason ? 'opacity-60' : 'cursor-pointer hover:bg-muted/50'}
                onClick={() => !cat.unscoredReason && onSelect(cat)}
              >
                <TableCell className="font-medium max-w-[260px] truncate" title={cat.categoryPath}>
                  {cat.categoryPath}
                </TableCell>
                <TableCell className="text-right">
                  {cat.chs !== null ? <ScoreBar score={cat.chs} /> : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <Sparkline values={categoryHistory[cat.categoryPath] ?? []} />
                </TableCell>
                <TableCell><TierBadge tier={cat.tier} /></TableCell>
                <TableCell className="text-right tabular-nums">{cat.totalProducts.toLocaleString()}</TableCell>
                <TableCell className="text-right tabular-nums">{cat.cl4HighProducts.toLocaleString()}</TableCell>
                <TableCell className="text-xs text-orange-700 max-w-[200px] truncate">
                  {cat.topMissingAttrs.slice(0, 2).map(a => a.attr).join(', ') || '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {cat.impactScore.toFixed(0)}
                </TableCell>
              </TableRow>
            ))}
            {categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No categories found for this site.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ============================================================================
// Product table
// ============================================================================

function ProductTable({
  site,
  category,
  products,
  onBack,
  onSelectProduct,
}: {
  site: SiteRow;
  category: CategoryRow;
  products: ProductRow[];
  onBack: () => void;
  onSelectProduct: (p: ProductRow) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ChevronLeft className="h-4 w-4" /> {site.siteName}
        </Button>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold truncate max-w-[260px]" title={category.categoryPath}>
          {category.categoryPath}
        </span>
        <TierBadge tier={category.tier} />
        {category.chs !== null && (
          <span className="text-sm text-muted-foreground">{Math.round(category.chs * 100)}% CHS</span>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Areas for improvement first ({products.length.toLocaleString()} products). Click a row for attribute detail.
      </p>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead className="text-right">PHS</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead className="text-right">Missing</TableHead>
              <TableHead className="text-right">Malformed</TableHead>
              <TableHead>CL4</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map(product => (
              <TableRow
                key={product.pfiId}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onSelectProduct(product)}
              >
                <TableCell className="font-medium max-w-[280px] truncate" title={product.productTitle}>
                  {product.productTitle}
                </TableCell>
                <TableCell className="text-muted-foreground">{product.brand ?? '—'}</TableCell>
                <TableCell className="text-right"><ScoreBar score={product.phs} /></TableCell>
                <TableCell><TierBadge tier={product.tier} /></TableCell>
                <TableCell className="text-right tabular-nums text-orange-600">
                  {product.missingAttrs.length}
                </TableCell>
                <TableCell className="text-right tabular-nums text-yellow-700">
                  {product.malformedAttrs.length}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{product.cl4Label ?? '—'}</TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No products found for this category.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ============================================================================
// Attribute detail panel
// ============================================================================

function AttributePanel({
  product,
  detail,
  loading,
  error,
  onClose,
}: {
  product: ProductRow;
  detail: AttributeDetail | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-background border-l shadow-xl z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate" title={product.productTitle}>
            {product.productTitle}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <TierBadge tier={product.tier} />
            <span className="text-xs text-muted-foreground">{Math.round(product.phs * 100)}% PHS</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="ml-2 shrink-0">✕</Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading && (
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {detail && !loading && (
          <div className="space-y-1">
            {detail.attributes
              .sort((a, b) => a.priorityRank - b.priorityRank)
              .map(attr => {
                const rowBg =
                  attr.status === 'truly_missing'  ? 'bg-red-50' :
                  attr.status === 'name_mismatch'  ? 'bg-amber-50' :
                  attr.status === 'malformed'      ? 'bg-yellow-50' :
                  attr.status === 'excluded'       ? 'bg-gray-50 opacity-60' : 'bg-green-50';
                return (
                  <div key={attr.attributeName} className={`flex items-start gap-3 p-2 rounded text-sm ${rowBg}`}>
                    <span className="mt-0.5 shrink-0 w-4 text-center">
                      {attr.status === 'present'       && <span className="text-green-600 font-bold">✓</span>}
                      {attr.status === 'name_mismatch' && <span className="text-amber-600 font-bold">~</span>}
                      {attr.status === 'truly_missing' && <span className="text-red-600 font-bold">✗</span>}
                      {attr.status === 'malformed'     && <span className="text-orange-500 font-bold">⚠</span>}
                      {attr.status === 'excluded'      && <span className="text-gray-400 font-bold">–</span>}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${attr.isKeyAttribute ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {attr.canonicalName}
                        {attr.isKeyAttribute && <span className="ml-1 text-xs text-orange-600">key</span>}
                      </p>
                      {attr.status === 'name_mismatch' && (
                        <p className="text-xs text-amber-700">
                          Found under a different key name — consider aligning to canonical
                        </p>
                      )}
                      {attr.status === 'truly_missing' && (
                        <p className="text-xs text-red-600">Missing — add this attribute to your feed</p>
                      )}
                      {attr.status === 'malformed' && attr.currentValue && (
                        <p className="text-xs text-orange-600 truncate" title={attr.currentValue}>
                          Current value: {attr.currentValue}
                        </p>
                      )}
                      {attr.status === 'excluded' && (
                        <p className="text-xs text-gray-400">Excluded from scoring for this site</p>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

type DrillState =
  | { level: 'sites' }
  | { level: 'categories'; site: SiteRow }
  | { level: 'products'; site: SiteRow; category: CategoryRow };

export default function CatalogHealthDashboard() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [drill, setDrill] = useState<DrillState>({ level: 'sites' });

  const [sites, setSites]           = useState<SiteRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [products, setProducts]     = useState<ProductRow[]>([]);
  const [categoryHistory, setCategoryHistory] = useState<Record<string, number[]>>({});
  const [categoryHistoryError, setCategoryHistoryError] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const [excludedAttrs, setExcludedAttrs]   = useState<string[]>([]);
  const [excludedLoading, setExcludedLoading] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState<ProductRow | null>(null);
  const [attrDetail, setAttrDetail]            = useState<AttributeDetail | null>(null);
  const [attrLoading, setAttrLoading]          = useState(false);
  const [attrError, setAttrError]              = useState<string | null>(null);

  const loadSites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setSites(await fetchSites()); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);

  const drillToCategories = useCallback(async (site: SiteRow) => {
    setLoading(true);
    setError(null);
    setExcludedAttrs([]);
    setExcludedLoading(isSuperAdmin);
    try {
      // scoring-config endpoint is super_admin-only; non-super-admin callers get 403.
      // Timeline is supplementary (sparklines only): a failure must NOT block the category table,
      // but it must NOT be silently swallowed either — a 403/500 is surfaced as a distinct
      // "trend unavailable" warning so it can't masquerade as "no history" (codex #4064 BLOCK).
      const [cats, excl, timelineRes] = await Promise.all([
        fetchCategories(site.siteId),
        isSuperAdmin ? fetchScoringConfig(site.siteId) : Promise.resolve([]),
        fetchTimeline(site.siteId).then(
          data => ({ data, error: null as string | null }),
          err => ({ data: [] as TimelineRow[], error: err instanceof Error ? err.message : String(err) }),
        ),
      ]);
      setCategories(cats);
      setExcludedAttrs(excl);
      setCategoryHistory(buildCategoryHistory(timelineRes.data));
      setCategoryHistoryError(timelineRes.error);
      setDrill({ level: 'categories', site });
    }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); setExcludedLoading(false); }
  }, [isSuperAdmin]);

  const drillToProducts = useCallback(async (site: SiteRow, category: CategoryRow) => {
    setLoading(true);
    setError(null);
    try {
      setProducts(await fetchProducts(site.siteId, category.categoryPath));
      setDrill({ level: 'products', site, category });
    }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);

  const openAttributePanel = useCallback(async (product: ProductRow) => {
    setSelectedProduct(product);
    setAttrDetail(null);
    setAttrError(null);
    setAttrLoading(true);
    try { setAttrDetail(await fetchAttributeDetail(product.pfiId)); }
    catch (e) { setAttrError(e instanceof Error ? e.message : String(e)); }
    finally { setAttrLoading(false); }
  }, []);

  useEffect(() => { loadSites(); }, [loadSites]);

  return (
    <>
      <div className={`p-6 space-y-6 max-w-screen-2xl mx-auto transition-all ${selectedProduct ? 'mr-96' : ''}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Catalog Health</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Areas for improvement, sorted by impact on high-demand products.
            </p>
          </div>
          {drill.level === 'sites' && (
            <Button variant="outline" size="sm" onClick={loadSites} disabled={loading} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
              {(error.includes('404') || error.includes('HTTP')) && (
                <span className="block mt-1 text-xs opacity-75">
                  Scoring API may not be running — check /api/admin/catalog-health/sites on p4.
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {loading && (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-lg" />)}
          </div>
        )}

        {!loading && !error && (
          <>
            {drill.level === 'sites' && (
              <SiteGrid sites={sites} onSelect={drillToCategories} />
            )}
            {drill.level === 'categories' && (
              <CategoryTable
                site={drill.site}
                categories={categories}
                categoryHistory={categoryHistory}
                categoryHistoryError={categoryHistoryError}
                onSelect={(cat) => drillToProducts(drill.site, cat)}
                onBack={() => { setDrill({ level: 'sites' }); setExcludedAttrs([]); setCategoryHistory({}); setCategoryHistoryError(null); }}
                excludedAttrs={excludedAttrs}
                excludedLoading={excludedLoading}
                isSuperAdmin={isSuperAdmin}
                onExcludedSaved={setExcludedAttrs}
              />
            )}
            {drill.level === 'products' && (
              <ProductTable
                site={drill.site}
                category={drill.category}
                products={products}
                onBack={() => setDrill({ level: 'categories', site: drill.site })}
                onSelectProduct={openAttributePanel}
              />
            )}
          </>
        )}

        {!loading && !error && drill.level === 'sites' && sites.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <p className="font-medium">No scoring data available yet.</p>
              <p className="text-sm mt-1">
                The scoring engine will populate this dashboard after the first daily run.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {selectedProduct && (
        <AttributePanel
          product={selectedProduct}
          detail={attrDetail}
          loading={attrLoading}
          error={attrError}
          onClose={() => { setSelectedProduct(null); setAttrDetail(null); }}
        />
      )}
    </>
  );
}
